import { useCallback, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { isValidTimeZone, viewerTimeZone } from "@/utils/temporal";
import {
  FOLLOW_BROWSER,
  cachedBrowserTimeZone,
  currentViewerKey,
  currentViewerTimeZoneOverride,
  subscribeToViewerTimeZone,
  writeViewerTimeZonePreference,
  type ViewerTimeZonePreference,
} from "@/utils/viewerTimeZone";

// The zone this viewer's instants should be rendered in (#11055).
//
// A zone the viewer has pinned wins, because they said so: someone who travels
// but keeps working to home time, or who works to a client's calendar, cannot
// be served by reading the browser. Nothing is pinned by default.
//
// The browser's zone is the default because it follows the person: someone who
// flies somewhere else sees their new local days on the next load. That is
// also why the pin is stored client-side and never on the user row -- the
// backend stays organization-only.
//
// The organization's zone is the fallback for a browser that will not name
// one, and the backend resolves that (Eastern when the organization has set
// none), so nothing here has to know what the default is.

interface CurrentTimezoneResponse {
  timezone: string;
}

export const ORGANIZATION_TIMEZONE_QUERY_KEY = [
  "organizations",
  "current",
  "timezone",
] as const;

const fetchOrganizationTimeZone =
  async (): Promise<CurrentTimezoneResponse> => {
    const response = await fpFetch(
      `${BASE_API_URL_V2}/api/v2/organizations/current/timezone`,
      { method: "GET", headers: { "Content-Type": "application/json" } }
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch organization timezone: ${response.status}`
      );
    }
    return response.json();
  };

/**
 * The pinned zone, or `null` when this viewer follows their browser.
 *
 * Subscribed rather than read once, so pinning a zone re-renders every date on
 * the page instead of waiting for the next navigation. The snapshot is a
 * string so React's identity check settles immediately.
 */
const usePinnedTimeZone = (): string | null =>
  useSyncExternalStore(
    subscribeToViewerTimeZone,
    currentViewerTimeZoneOverride,
    currentViewerTimeZoneOverride
  );

export const useViewerTimeZone = (): string => {
  const pinned = usePinnedTimeZone();
  const browser = cachedBrowserTimeZone();
  const { data } = useQuery({
    queryKey: ORGANIZATION_TIMEZONE_QUERY_KEY,
    queryFn: fetchOrganizationTimeZone,
    // Only asked for when nothing has been pinned and the browser will not
    // say, which is close to never. An organization's zone then changes about
    // never in turn, so one fetch covers the session and a failure falls
    // through to Eastern rather than retrying behind every row on the page.
    enabled: !pinned && !isValidTimeZone(browser),
    staleTime: Infinity,
    retry: false,
  });
  return pinned ?? viewerTimeZone(data?.timezone, browser);
};

/** The organization's zone as a schedule form needs it. */
export interface OrganizationTimeZone {
  /** Undefined while it loads, or when the read failed. */
  zone: string | undefined;
  /** The read failed; `retry` asks again. */
  failed: boolean;
  retry: () => void;
}

/**
 * The organization's zone, for a schedule form.
 *
 * Not a display zone: it is the zone server-initiated work runs in. A schedule
 * fires at its time of day in its own zone, which for a new schedule is the
 * organization's, so a schedule form reads and names its time here. Reading
 * it in the viewer's zone stored a Pacific teammate's "8:30" for an Eastern
 * client as 11:30 Eastern (#12225). No fallback is guessed: a form waits for
 * the answer rather than store a time in a zone nobody chose, and says so when
 * the read failed rather than look like it is still loading.
 */
export const useOrganizationTimeZone = (
  enabled: boolean = true
): OrganizationTimeZone => {
  const { data, isError, refetch } = useQuery({
    queryKey: ORGANIZATION_TIMEZONE_QUERY_KEY,
    queryFn: fetchOrganizationTimeZone,
    // Asked for only when a schedule needs it: a form whose schedule already
    // names its zone, or that is not scheduling at all, has no use for it.
    enabled,
    staleTime: Infinity,
    retry: false,
  });
  return {
    zone: data?.timezone,
    failed: isError,
    retry: () => void refetch(),
  };
};

/**
 * The viewer's own preference, for the control that sets it.
 *
 * Returns the preference rather than the resolved zone: a picker has to be
 * able to tell "pinned to the browser's current zone" from "following the
 * browser", which the resolved string cannot express.
 */
export const useViewerTimeZonePreference = (): [
  ViewerTimeZonePreference,
  (preference: ViewerTimeZonePreference) => void,
] => {
  const pinned = usePinnedTimeZone();
  const preference: ViewerTimeZonePreference = pinned
    ? { kind: "fixed", zone: pinned }
    : FOLLOW_BROWSER;
  const setPreference = useCallback(
    (next: ViewerTimeZonePreference) =>
      writeViewerTimeZonePreference(currentViewerKey(), next),
    []
  );
  return [preference, setPreference];
};
